import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, App, Button, Card, Col, DatePicker, Flex, Input, InputNumber, Result, Row, Segmented, Skeleton, Space, Table, Tag, Typography } from "antd";
import type { Dayjs } from "dayjs";
import { useRef, useState, type ComponentRef } from "react";
import { useNavigate, useParams } from "react-router";
import { ApiError, api, unwrap } from "@/api/client";
import { useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { ScanInput } from "@/components/ScanInput";
import { useTabTitle } from "@/components/tabs/WorkTabs";
import { useI18n } from "@/i18n";
import { SkuThumb } from "@/components/SkuThumb";
import { ReceiptStatusTag, type Receipt } from "./common";
import { SerialImportModal } from "./SerialImport";

type Line = NonNullable<Receipt["lines"]>[number];
type Scanned = { id: string; code: string; name: string; tracking: "none" | "lot" | "serial" };

/** Penerimaan detail: scan goods in, then complete (App Flow §8.1). */
export function InboundDetail() {
  const { receiptID = "" } = useParams();
  const { t, formatNumber, formatDateTime, formatMoney } = useI18n();
  const { me, can } = useAuth();
  const { message, modal } = App.useApp();
  const errorText = useErrorText();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const tz = me?.workspace?.timezone;
  const [sku, setSku] = useState<Scanned | null>(null);
  const [condition, setCondition] = useState<"sellable" | "damaged" | "quarantine">("sellable");
  const [qty, setQty] = useState<number | null>(1);
  const [lotNo, setLotNo] = useState("");
  const [expires, setExpires] = useState<Dayjs | null>(null);
  const [serials, setSerials] = useState<string[]>([]);
  // Serial SKUs are received by count; scanning each IMEI now is optional.
  const [scanSerials, setScanSerials] = useState(false);
  const [importing, setImporting] = useState<Line | null>(null);
  const [putawayTask, setPutawayTask] = useState<string | null>(null);
  // Receiving on pallets: the pallet the next goods go on.
  const [pallet, setPallet] = useState<string | null>(null);
  const qtyRef = useRef<ComponentRef<typeof InputNumber>>(null);

  const receipt = useQuery({
    queryKey: ["inbound", "detail", receiptID],
    queryFn: () => unwrap(api.GET("/v1/inbound/{receiptID}", { params: { path: { receiptID } } })),
    retry: (n, err) => !(err instanceof ApiError && err.status === 404) && n < 2,
  });
  useTabTitle(receipt.data?.number);
  const r = receipt.data;
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["inbound"] });
    void queryClient.invalidateQueries({ queryKey: ["inventory"] });
    void queryClient.invalidateQueries({ queryKey: ["tasks"] });
  };
  const reset = () => {
    setSku(null);
    setQty(1);
    setLotNo("");
    setExpires(null);
    setSerials([]);
    setScanSerials(false);
    setCondition("sellable");
  };

  // Resolves true or false: the scan field beeps accordingly.
  const scan = async (code: string): Promise<boolean> => {
    if (!r) return false;
    // While a serial SKU is chosen, scans are its units.
    if (sku?.tracking === "serial" && scanSerials) {
      setSerials((s) => (s.includes(code) ? s : [...s, code]));
      return true;
    }
    try {
      const hit = await unwrap(api.GET("/v1/scan", { params: { query: { warehouseId: r.warehouseId, code } } }));
      if (r.palletReceiving && hit.type === "location" && hit.location?.kind === "pallet") {
        setPallet(hit.location.code ?? code);
        void message.success(t("wms.plt.current", { code: hit.location.code ?? code }));
        return true;
      }
      if (hit.type !== "sku" || !hit.sku) {
        void message.warning(t("error.code_not_found"));
        return false;
      }
      setSku({ id: hit.sku.id!, code: hit.sku.code!, name: hit.sku.name!, tracking: hit.sku.tracking ?? "none" });
      setQty(1);
      setSerials([]);
      setTimeout(() => qtyRef.current?.focus(), 0);
      return true;
    } catch (err) {
      void message.error(errorText(err));
      return false;
    }
  };

  const receive = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST("/v1/inbound/{receiptID}/receive", {
          params: { path: { receiptID } },
          body: {
            skuId: sku!.id,
            condition,
            ...(r?.palletReceiving && condition === "sellable" && pallet ? { palletCode: pallet } : {}),
            ...(sku!.tracking === "serial" && scanSerials ? { serials } : { quantity: qty ?? 0 }),
            ...(sku!.tracking === "lot" ? { lot: { no: lotNo.trim(), ...(expires ? { expiresOn: expires.format("YYYY-MM-DD") } : {}) } } : {}),
          },
        }),
      ),
    onSuccess: () => {
      void message.success(t("wms.in.received1", { qty: sku!.tracking === "serial" && scanSerials ? serials.length : (qty ?? 0), code: sku!.code }));
      reset();
      refresh();
    },
    onError: (err) => void message.error(errorText(err)),
  });

  const complete = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/inbound/{receiptID}/complete", { params: { path: { receiptID } } })),
    onSuccess: (res) => {
      void message.success(t("wms.in.completed"));
      setPutawayTask(res.putawayTaskId ?? null);
      refresh();
    },
    onError: (err) => void message.error(errorText(err)),
  });
  const cancel = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/inbound/{receiptID}/cancel", { params: { path: { receiptID } } })),
    onSuccess: refresh,
    onError: (err) => void message.error(errorText(err)),
  });

  if (receipt.isError) {
    return receipt.error instanceof ApiError && receipt.error.status === 404 ? <Result status="404" title={t("page.notFound.title")} /> : <Result status="error" title={errorText(receipt.error)} />;
  }
  if (!r) return <Skeleton active />;
  const open = r.status === "open" || r.status === "receiving";
  const scanning = sku?.tracking === "serial" && scanSerials;
  const ready = sku && (scanning ? serials.length > 0 : (qty ?? 0) > 0) && (sku.tracking !== "lot" || (lotNo.trim() && expires));

  return (
    <>
      <Flex justify="space-between" align="center" wrap gap={12} style={{ marginBottom: 16 }}>
        <Space align="center" size={12} wrap>
          <Typography.Title level={2} className="z-num" style={{ margin: 0, fontSize: 28 }}>
            {r.number}
          </Typography.Title>
          <ReceiptStatusTag status={r.status} />
          <Typography.Text type="secondary">
            {[r.supplier, r.reference, r.warehouseName].filter(Boolean).join(" · ")} · {formatDateTime(r.createdAt, tz)}
          </Typography.Text>
        </Space>
        {open && (
          <Space>
            {r.status === "open" && can("warehouse.manage") && (
              <Button danger loading={cancel.isPending} onClick={() => cancel.mutate()}>
                {t("wms.in.cancel")}
              </Button>
            )}
            <Button
              type="primary"
              loading={complete.isPending}
              onClick={() =>
                modal.confirm({ title: t("wms.in.complete"), content: t("wms.in.completeConfirm", { number: r.number }), okText: t("wms.in.complete"), cancelText: t("common.cancel"), onOk: () => complete.mutateAsync() })
              }
            >
              {t("wms.in.complete")}
            </Button>
          </Space>
        )}
      </Flex>
      {putawayTask && (
        <Alert
          type="success"
          showIcon
          style={{ marginBottom: 16 }}
          title={t("wms.in.completed")}
          action={<Button onClick={() => navigate(`/app/warehouse/tasks/${putawayTask}`)}>{t("wms.in.openPutaway")}</Button>}
        />
      )}
      <Row gutter={[16, 16]}>
        {open && (
          <Col xs={24} xl={9}>
            <Card title={t("wms.in.scanTitle")} size="small">
              <Space orientation="vertical" size={12} style={{ width: "100%" }}>
                <Typography.Text type="secondary">{t("wms.in.scanHelp")}</Typography.Text>
                {r.palletReceiving && (
                  <Alert
                    type={pallet ? "info" : "warning"}
                    showIcon
                    title={pallet ? t("wms.plt.current", { code: pallet }) : t("wms.plt.scanFirst", { n: r.palletMaxSkus ?? 4 })}
                    action={pallet && <Button size="small" onClick={() => setPallet(null)}>{t("wms.plt.change")}</Button>}
                  />
                )}
                <ScanInput onScan={scan} />
                {sku && (
                  <Card size="small" type="inner" title={<span className="z-num">{sku.code}</span>} extra={<Button size="small" type="text" onClick={reset}>{t("common.cancel")}</Button>}>
                    <Space orientation="vertical" size={8} style={{ width: "100%" }}>
                      <Typography.Text>{sku.name}</Typography.Text>
                      {sku.tracking === "lot" && (
                        <Flex gap={8}>
                          <Input placeholder={t("wms.in.lotNo")} value={lotNo} onChange={(e) => setLotNo(e.target.value)} />
                          <DatePicker placeholder={t("wms.in.expiresOn")} value={expires} onChange={setExpires} />
                        </Flex>
                      )}
                      {sku.tracking === "serial" && (
                        <Segmented
                          block
                          value={scanSerials ? "scan" : "count"}
                          onChange={(v) => {
                            setScanSerials(v === "scan");
                            setSerials([]);
                          }}
                          options={[
                            { value: "count", label: t("serial.receive.count") },
                            { value: "scan", label: t("serial.receive.scan") },
                          ]}
                        />
                      )}
                      {scanning ? (
                        <>
                          <Typography.Text type="secondary">{t("wms.in.serialsHelp", { n: serials.length })}</Typography.Text>
                          <Space wrap>
                            {serials.map((s) => (
                              <Tag key={s} closable onClose={() => setSerials((x) => x.filter((y) => y !== s))} className="z-num">
                                {s}
                              </Tag>
                            ))}
                          </Space>
                        </>
                      ) : (
                        <InputNumber
                          ref={qtyRef}
                          prefix={t("wms.in.quantity")}
                          min={1}
                          max={100_000}
                          precision={0}
                          value={qty}
                          onChange={setQty}
                          onPressEnter={() => ready && receive.mutate()}
                          style={{ width: "100%" }}
                        />
                      )}
                      <Segmented
                        block
                        value={condition}
                        onChange={setCondition}
                        options={(["sellable", "damaged", "quarantine"] as const).map((c) => ({ value: c, label: t(`wms.in.condition.${c}`) }))}
                      />
                      <Button type="primary" block disabled={!ready} loading={receive.isPending} onClick={() => receive.mutate()}>
                        {t("wms.in.receive")}
                        {scanning ? ` (${serials.length})` : ""}
                      </Button>
                    </Space>
                  </Card>
                )}
              </Space>
            </Card>
          </Col>
        )}
        <Col xs={24} xl={open ? 15 : 24}>
          <Card size="small" title={t("wms.in.lines")}>
            <Table<Line>
              size="small"
              rowKey="id"
              dataSource={r.lines ?? []}
              pagination={false}
              columns={[
                { title: t("catalog.code"), dataIndex: "skuCode", render: (v: string, l) => <Space><SkuThumb skuId={l.skuId} size={32} /><span className="z-num">{v}</span></Space> },
                { title: t("common.name"), dataIndex: "skuName" },
                {
                  title: t("catalog.tracking"),
                  dataIndex: "tracking",
                  width: 170,
                  render: (v: Line["tracking"], l) =>
                    v === "none" ? (
                      "—"
                    ) : v === "serial" ? (
                      <Space orientation="vertical" size={2}>
                        <Tag color={(l.serials ?? 0) < l.received ? "warning" : "success"} className="z-num">
                          {t("serial.receiptCount", { n: formatNumber(l.serials ?? 0), of: formatNumber(l.received) })}
                        </Tag>
                        {(l.serials ?? 0) < l.received && r.status !== "cancelled" && (
                          <Button size="small" onClick={() => setImporting(l)}>
                            {t("serial.import.button")}
                          </Button>
                        )}
                      </Space>
                    ) : (
                      <Tag color="processing">{t(`catalog.tracking.${v}`)}</Tag>
                    ),
                },
                { title: t("wms.in.expected"), dataIndex: "expected", width: 110, align: "right", render: (v: number) => <span className="z-num">{formatNumber(v)}</span> },
                {
                  title: t("wms.in.received"),
                  dataIndex: "received",
                  width: 150,
                  align: "right",
                  render: (v: number, l) => (
                    <Space orientation="vertical" size={0} style={{ alignItems: "flex-end" }}>
                      <span className="z-num">{formatNumber(v)}</span>
                      {l.damaged > 0 && <Tag color="error" style={{ marginInlineEnd: 0 }}>{t("wms.in.damagedN", { n: l.damaged })}</Tag>}
                      {l.quarantine > 0 && <Tag color="warning" style={{ marginInlineEnd: 0 }}>{t("wms.in.quarantineN", { n: l.quarantine })}</Tag>}
                    </Space>
                  ),
                },
                ...(can("warehouse.manage")
                  ? [
                      {
                        title: t("wms.in.unitCost"),
                        dataIndex: "unitCost",
                        width: 140,
                        align: "right" as const,
                        render: (v: number | null | undefined) => (v == null ? <Typography.Text type="secondary">—</Typography.Text> : <span className="z-num">{formatMoney(v)}</span>),
                      },
                    ]
                  : []),
                {
                  title: t("wms.in.difference"),
                  width: 110,
                  align: "right",
                  render: (_, l) => {
                    const d = l.received - l.expected;
                    return (
                      <Typography.Text className="z-num" type={d === 0 ? "secondary" : d < 0 ? "warning" : "danger"}>
                        {d > 0 ? `+${d}` : d}
                      </Typography.Text>
                    );
                  },
                },
              ]}
            />
          </Card>
        </Col>
      </Row>
      {importing && (
        <SerialImportModal
          title={t("serial.import.titleFor", { code: importing.skuCode })}
          help={t("serial.import.receiptHelp")}
          max={importing.received - (importing.serials ?? 0)}
          onClose={() => setImporting(null)}
          onSubmit={async (list) => {
            await unwrap(api.POST("/v1/inbound/{receiptID}/serials", { params: { path: { receiptID } }, body: { skuId: importing.skuId, serials: list } }));
            void message.success(t("serial.import.done", { n: list.length }));
            refresh();
          }}
        />
      )}
    </>
  );
}
